import React, { useState } from 'react';
import {
  Search, Wrench, Users, Calendar,
  Globe, Building2, Loader2,
  CheckCircle, X, Shield, Lock, Unlock, AlertCircle,
  Save, Phone, Mail, MapPin, Image, Share2, Ticket, FileText, Hash
} from 'lucide-react';
import { useBusinessRestrictions } from '../../authentication_components/prlocalRestrictionContext';
import { useAuth } from '../../authentication_components/authContext';

// =================================================================
// CONFIGURATION
// =================================================================
const RESTRICTION_MODULES = [
  { key: 'public_appearance_restriction', title: 'Perfil Público', description: 'Visibilidad general del negocio en la aplicación.', icon: Globe },
  { key: 'business_phone_restriction', title: 'Teléfono de Negocio', description: 'Visualización del contacto telefónico.', icon: Phone },
  { key: 'business_email_restriction', title: 'Email de Negocio', description: 'Visualización del correo electrónico de contacto.', icon: Mail },
  { key: 'business_location_restriction', title: 'Ubicación', description: 'Visualización de la dirección física y mapa.', icon: MapPin },
  { key: 'gallery_images_restriction', title: 'Galería de Imágenes', description: 'Capacidad para subir y mostrar fotos del negocio.', icon: Image },
  { key: 'services_restriction', title: 'Servicios', description: 'Listado de servicios y sistema de citas.', icon: Wrench },
  { key: 'schedule_restriction', title: 'Horario Laboral', description: 'Visualización de horas de operación.', icon: Calendar },
  { key: 'team_restriction', title: 'Equipo de Trabajo', description: 'Gestión y visualización de empleados.', icon: Users },
  { key: 'event_restriction', title: 'Eventos', description: 'Publicación de eventos en el calendario.', icon: Calendar },
  { key: 'event_ticket_restriction', title: 'Venta de Boletos', description: 'Capacidad para vender entradas a eventos.', icon: Ticket },
  { key: 'socials_restriction', title: 'Redes Sociales', description: 'Enlaces a plataformas externas (IG, FB, etc).', icon: Share2 }
];

// =================================================================
// UI SUB-COMPONENTS
// =================================================================

const ToggleSwitch = ({ isEnabled, onChange, disabled = false }) => (
  <button
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${isEnabled ? 'bg-emerald-500' : 'bg-slate-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const ModuleCard = ({ title, description, icon: Icon, isEnabled, isModified, onToggle }) => (
  <div className={`relative overflow-hidden rounded-2xl border transition-all duration-200 ${isEnabled ? 'bg-white border-slate-200 shadow-sm hover:shadow-md' : 'bg-slate-50 border-slate-200 opacity-90'
    } ${isModified ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}>
    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${isEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
    <div className="p-5 pl-7">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl ${isEnabled ? 'bg-violet-50 text-violet-600' : 'bg-slate-200 text-slate-500'}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex flex-col items-end gap-1">
          <ToggleSwitch isEnabled={isEnabled} onChange={onToggle} />
          {isModified && <span className="text-[10px] font-bold text-amber-600 uppercase">Modificado</span>}
        </div>
      </div>
      <h3 className={`font-bold text-lg mb-1 ${isEnabled ? 'text-slate-800' : 'text-slate-500'}`}>{title}</h3>
      <p className="text-sm text-slate-500 mb-4 min-h-[40px]">{description}</p>
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-400">
          {isEnabled ? <><Unlock className="h-3 w-3" /> Activo</> : <><Lock className="h-3 w-3" /> Restringido</>}
        </div>
      </div>
    </div>
  </div>
);

const SaveChangesDialog = ({ isOpen, onClose, onConfirm, pendingCount, isSaving }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError('Debe ingresar una razón para aplicar estos cambios.');
      return;
    }
    onConfirm(reason);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4 text-violet-600">
            <div className="p-2 bg-violet-100 rounded-full"><Save className="h-6 w-6" /></div>
            <h3 className="text-lg font-bold text-slate-800">Guardar Cambios</h3>
          </div>
          <p className="text-slate-600 mb-4">Está a punto de aplicar <strong>{pendingCount} cambios</strong>.</p>
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">Razón del cambio (Requerido)</label>
            <textarea value={reason} onChange={(e) => { setReason(e.target.value); setError(''); }} className="w-full border border-slate-300 rounded-xl p-3 text-sm focus:ring-2 focus:ring-violet-500 focus:outline-none h-24 resize-none" disabled={isSaving} />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} disabled={isSaving} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50">Cancelar</button>
            <button onClick={handleConfirm} disabled={isSaving} className="flex-1 px-4 py-2.5 bg-violet-600 text-white font-medium rounded-xl hover:bg-violet-700 shadow-md shadow-violet-200 transition-colors flex justify-center items-center gap-2 disabled:opacity-50">{isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmar'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// MAIN COMPONENT
// =================================================================
export default function BusinessContentManager() {
  const { getRestrictionsByProfile, createRestriction, updateRestriction, fetchBusinessNameByProfileId } = useBusinessRestrictions();
  const { adminId } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentData, setCurrentData] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [notFoundBusinessName, setNotFoundBusinessName] = useState('');

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleSearch = async (e) => {
    e?.preventDefault(); // Prevent form submission if called from form

    if (!searchTerm.trim()) {
      setError('Por favor ingrese un Profile ID');
      return;
    }

    setStatus('loading');
    setCurrentData(null);
    setNotFoundBusinessName('');
    setPendingChanges({}); // Clear any pending changes
    setError(null); // Clear previous errors

    try {
      const result = await getRestrictionsByProfile(searchTerm.trim());

      if (result) {
        setCurrentData(result);
        setStatus('loaded');
      } else {
        // Not found - fetch business name separately
        const businessName = await fetchBusinessNameByProfileId(searchTerm.trim());
        setNotFoundBusinessName(businessName || searchTerm.trim());
        setStatus('not_found');
      }
    } catch (error) {
      console.error('Search error:', error);

      // Try to fetch business name even on error
      try {
        const businessName = await fetchBusinessNameByProfileId(searchTerm.trim());
        setNotFoundBusinessName(businessName || searchTerm.trim());
      } catch (nameError) {
        console.error('Error fetching business name:', nameError);
        setNotFoundBusinessName(searchTerm.trim());
      }

      setError(error.message || 'Error al buscar el perfil');
      setStatus('not_found');
    }
  };

  const handleInitialize = async () => {
    try {
      setStatus('saving');
      await createRestriction({ profile_id: searchTerm.trim(), admin_id: adminId });
      const data = await getRestrictionsByProfile(searchTerm.trim());
      setCurrentData(data);
      setStatus('loaded');
    } catch (err) {
      setError(err.message);
      setStatus('not_found');
    }
  };

  const handleToggle = (key) => {
    if (!currentData) return;
    const dbValue = currentData[key];
    const currentEffectiveValue = pendingChanges.hasOwnProperty(key) ? pendingChanges[key] : dbValue;
    const newValue = !currentEffectiveValue;
    setPendingChanges(prev => {
      const newChanges = { ...prev };
      if (newValue === currentData[key]) delete newChanges[key];
      else newChanges[key] = newValue;
      return newChanges;
    });
  };

  const executeSave = async (reason) => {
    if (!currentData || !hasChanges) return;
    const payload = { ...pendingChanges, admin_id: adminId };
    try {
      setStatus('saving');
      await updateRestriction(currentData.restriction_id, payload);
      const refreshedData = await getRestrictionsByProfile(currentData.profile_id);
      setCurrentData(refreshedData);
      setPendingChanges({});
      setShowSaveDialog(false);
      setStatus('loaded');
    } catch (err) {
      setError('Error guardando cambios: ' + err.message);
      setStatus('loaded');
      setShowSaveDialog(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-24">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Gestor de Restricciones</h1>
          <form onSubmit={handleSearch} className="relative max-w-2xl">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Ingrese Profile ID del Negocio..."
              className="block w-full pl-10 pr-32 py-3 border border-slate-200 rounded-xl leading-5 bg-slate-50 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-violet-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="submit"
              disabled={status === 'loading' || !searchTerm.trim()}
              className="absolute right-1.5 top-1.5 bottom-1.5 px-4 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
            </button>
          </form>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center text-red-700 mb-6"><AlertCircle className="h-5 w-5 mr-3" /><span className="font-medium">{error}</span><button onClick={() => setError(null)} className="ml-auto"><X className="h-4 w-4" /></button></div>}

        {status === 'idle' && (
          <div className="text-center py-20 opacity-60">
            <div className="bg-slate-100 p-4 rounded-full inline-block mb-4"><Building2 className="h-12 w-12 text-slate-400" /></div>
            <h3 className="text-lg font-semibold text-slate-600">Esperando Búsqueda</h3>
            <p className="text-slate-400 max-w-sm mx-auto mt-1">Ingrese un ID de perfil para ver o crear restricciones.</p>
          </div>
        )}

        {status === 'not_found' && (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 border-dashed">
            <div className="bg-amber-50 p-4 rounded-full inline-block mb-4">
              <Shield className="h-12 w-12 text-amber-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">
              No existen restricciones para:
              <span className="text-violet-600 ml-2">{notFoundBusinessName}</span>
            </h3>
            <p className="text-slate-500 max-w-md mx-auto mt-2 mb-6">
              No se encontró una configuración para el Profile ID:
              <span className="font-mono bg-slate-100 px-2 rounded">{searchTerm}</span>
            </p>
            <button
              onClick={handleInitialize}
              disabled={status === 'saving'}
              className="px-6 py-3 bg-violet-600 text-white rounded-xl font-bold shadow-lg shadow-violet-200 hover:bg-violet-700 transition-transform active:scale-95 flex items-center gap-2 mx-auto"
            >
              {status === 'saving' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Inicializar Restricciones'
              )}
            </button>
          </div>
        )}

        {status === 'loaded' && currentData && (
          <div className="animate-in fade-in duration-300">

            {/* UPDATED HEADER WITH BUSINESS INFO */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-8 flex flex-col md:flex-row justify-between gap-4">
              <div className="flex items-start gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">
                    {currentData.business_name || "Nombre de Negocio No Disponible"}
                  </h2>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 text-sm text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Hash className="h-4 w-4 text-slate-400" />
                      <span className="font-mono">ID: {currentData.profile_id}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-end">
                <div className="flex items-center gap-2">
                  {hasChanges ? (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Cambios pendientes
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-bold flex items-center gap-1 border border-emerald-100">
                      <CheckCircle className="h-3 w-3" /> Sincronizado
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {RESTRICTION_MODULES.map((module) => {
                const dbValue = currentData[module.key]; // true = restricted
                const pendingValue = pendingChanges[module.key];
                const isActiveEffective = pendingChanges.hasOwnProperty(module.key) ? !pendingValue : !dbValue;
                const isModified = pendingChanges.hasOwnProperty(module.key);
                return <ModuleCard key={module.key} title={module.title} description={module.description} icon={module.icon} isEnabled={isActiveEffective} isModified={isModified} onToggle={() => handleToggle(module.key)} />;
              })}
            </div>
          </div>
        )}
      </main>

      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40 animate-in slide-in-from-bottom-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-2 rounded-lg text-amber-600"><AlertCircle className="h-5 w-5" /></div>
              <div><p className="font-bold text-slate-800">Cambios sin guardar</p><p className="text-sm text-slate-500">Hay {Object.keys(pendingChanges).length} modificaciones pendientes.</p></div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setPendingChanges({})} className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-100 rounded-lg transition-colors">Descartar</button>
              <button onClick={() => setShowSaveDialog(true)} className="px-6 py-2 bg-violet-600 text-white font-bold rounded-lg shadow-md shadow-violet-200 hover:bg-violet-700 transition-colors flex items-center gap-2"><Save className="h-4 w-4" /> Guardar Cambios</button>
            </div>
          </div>
        </div>
      )}

      <SaveChangesDialog isOpen={showSaveDialog} pendingCount={Object.keys(pendingChanges).length} onClose={() => setShowSaveDialog(false)} onConfirm={executeSave} isSaving={status === 'saving'} />
    </div>
  );
}